import React from 'react';
import { Card, CardContent, Typography, Stack, Box, IconButton } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import CustomButton from '../Button/CustomButton';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { IActivityFeedItem } from '../../redux/activity/types';
import CustomLazyLoadImage from '../Image/CustomLazyLoadImage';
import { ThspianAvatar } from '../Avatar/ThspianAvatar';
import { convertMinuteSecondsDays, formatWithOrdinal, timeUntil } from '../../utils/AppUtils';
import moment from 'moment';
import CalendarIcon from '../CustomSVGIcons/CalendarIcon';
import { CustomChipGroup } from '../Chip/CustomChipGroup';
import BankNoteIcon from '../CustomSVGIcons/BankNoteIcon';
import { CutomReadMore } from '../ReadMore/CutomReadMore';
import { BackgroundColorCard } from './BackGroundColorCard';

export interface IJobProps {
    event: IActivityFeedItem;
    onRecommendClick: () => void;
    onRegisterEventClick: () => void;
}

const EventsCard: React.FC<IJobProps> = ({ event, onRecommendClick, onRegisterEventClick }) => {
    const { themeStyle } = useThemeContext();
    const { events_activity } = event;

    return (
        <Card
            sx={{
                width: '100%',
                borderRadius: 2,
                overflow: 'visible',
                my: 2,
                backgroundColor: theme[themeStyle].secondaryBg,
            }}>
            <CardContent sx={{ px: 0 }}>
                {/* Skill Match Percentage */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" px={2}>
                    <Stack direction="row" alignItems="center" gap={'10px'}>
                        <ThspianAvatar user={event?.created_by!} />
                        <Stack>
                            <Stack direction="row" gap={'3px'}>
                                <Typography
                                    style={{
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        color: theme[themeStyle].tag_white,
                                        fontSize: 10,
                                    }}>
                                    {event?.created_by.firstName}
                                </Typography>
                                <Typography
                                    style={{
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        color: theme[themeStyle].tag_white,
                                        fontSize: 10,
                                    }}>
                                    {event.activity_type}
                                </Typography>
                            </Stack>
                            <Stack alignItems="flex-start">
                                <Typography
                                    style={{
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        color: theme[themeStyle].tag_white,
                                        fontSize: 8,
                                    }}>
                                    {convertMinuteSecondsDays(event.createdAt)} ago
                                </Typography>
                            </Stack>
                        </Stack>
                    </Stack>

                    {/* Bookmark Icon */}
                    <IconButton>
                        <BookmarkBorderIcon sx={{ color: theme[themeStyle].tag_white }} />
                    </IconButton>
                </Stack>
                {events_activity?.feature_image && (
                    <Stack sx={{ maxHeight: '300px', overflow: 'hidden', width: '100%', my: 2 }} position="relative">
                        <Stack
                            position="absolute"
                            top={5}
                            right={5}
                            borderRadius={1}
                            bgcolor={'rgba(0,0,0,0.6)'}
                            justifyContent="center"
                            alignItems="center"
                            p={1}
                            zIndex={999}>
                            <Typography
                                sx={{
                                    fontSize: 10,
                                    color: theme[themeStyle].true_white,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                }}>
                                {timeUntil(events_activity.event_start_date)}
                            </Typography>
                        </Stack>
                        <CustomLazyLoadImage src={events_activity?.feature_image} />
                    </Stack>
                )}
                {!events_activity?.feature_image &&
                    events_activity?.is_background_color &&
                    events_activity.background_color && (
                        <BackgroundColorCard
                            backgroundColor={events_activity.background_color}
                            text={events_activity.name}
                        />
                    )}
                {/* Title */}
                <Stack alignItems="flex-start" px={2} pb={1}>
                    {events_activity?.feature_image &&
                        !events_activity?.is_background_color &&
                        !events_activity.background_color && (
                            <Typography
                                sx={{
                                    fontWeight: 600,
                                    marginBottom: 1,
                                    color: theme[themeStyle].tag_white,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                }}>
                                {events_activity?.name}
                            </Typography>
                        )}

                    {/* Description */}

                    <CutomReadMore
                        maxChars={120}
                        text={events_activity?.description!}
                        style={{
                            color: theme[themeStyle].tag_white,
                            marginBottom: 2,
                            textAlign: 'left',
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontSize: 12,
                        }}
                    />
                </Stack>

                {/* Tags */}
                <Stack direction="row" spacing={1} sx={{ marginBottom: 2 }} px={2}>
                    {events_activity && events_activity?.tags && (
                        <CustomChipGroup tags={events_activity?.tags} maxVisible={2} />
                    )}
                </Stack>

                {/* Details */}
                <Stack direction="row" alignItems="center" gap={'15px'} px={2}>
                    {/* Price and Type */}
                    <Stack direction="row" alignItems="center" gap={1}>
                        <CalendarIcon fill={theme[themeStyle].tag_white} height={'25px'} width={'25px'} />
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: 12,
                                color: theme[themeStyle].tag_white,
                            }}>
                            {moment(events_activity?.event_start_date).isSame(events_activity?.event_end_date, 'day')
                                ? `${formatWithOrdinal(events_activity?.event_start_date)}`
                                : `${formatWithOrdinal(events_activity?.event_start_date)} - ${formatWithOrdinal(
                                      events_activity?.event_end_date,
                                  )}`}
                        </Typography>
                    </Stack>

                    {/* Location */}
                    {events_activity && events_activity?.location && (
                        <Stack direction="row" alignItems="center" spacing={0.5} px={2}>
                            <LocationOnIcon sx={{ color: theme[themeStyle].tag_white }} />
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: 12,
                                    color: theme[themeStyle].tag_white,
                                    textTransform: 'capitalize',
                                    maxWidth: '350px',
                                    display: '-webkit-box',
                                    overflow: 'hidden',
                                    WebkitBoxOrient: 'vertical',
                                    WebkitLineClamp: 1, //numOfLines
                                }}>
                                {events_activity?.location.short_name
                                    ? events_activity?.location.short_name
                                    : events_activity?.location.address}
                            </Typography>
                        </Stack>
                    )}

                    {events_activity?.with_fee && (
                        <Stack direction="row" gap={0.2} alignItems="center">
                            <BankNoteIcon fill={theme[themeStyle].tag_white} height={'16px'} width={'16px'} />
                            <BankNoteIcon fill={theme[themeStyle].tag_white} height={'16px'} width={'16px'} />
                            <BankNoteIcon fill={theme[themeStyle].tag_white} height={'16px'} width={'16px'} />
                        </Stack>
                    )}
                </Stack>

                {/* Footer */}
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 2, pr: 1 }} px={2}>
                    {/* Avatar and Info */}
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <ThspianAvatar user={events_activity?.created_by} withMonogram enableDefaultNavigation />
                        <Box>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: 10,
                                    color: theme[themeStyle].tag_white,
                                }}>
                                {events_activity?.created_by.firstName}
                            </Typography>
                        </Box>
                    </Stack>
                </Stack>
                <Stack
                    direction={{
                        xs: 'column',
                        md: 'row',
                    }}
                    gap={'10px'}
                    px={2}
                    my={2}>
                    <CustomButton
                        label="Register"
                        onClick={onRegisterEventClick}
                        sx={{
                            flex: 1,
                        }}
                    />
                    <CustomButton
                        label="Get Tickets"
                        onClick={onRecommendClick}
                        buttonProps={{
                            variant: 'text',
                        }}
                        sx={{
                            border: `1px solid ${theme[themeStyle].highlight}`,
                            flex: 1,
                        }}
                    />
                </Stack>
            </CardContent>
        </Card>
    );
};

export { EventsCard };
